import { useSyncExternalStore } from 'react';
import type { Chat } from '@/data/types';
import { customerStatus } from './chatGroups';

/**
 * The chat list's filter (Gary, 18 Oct): status, owner, area and labels. Any picked value in a group matches
 * (Barry or Sue), and the groups narrow each other (Unpaid and Barry). Kept on this device.
 */
// 'show' holds "All open chats" (Gary, 19 Oct: All moved from the tabs into the filter): every open chat, any tab.
// 'brand' (3 Nov 2026): CleanMyBed or Peace of Sleep, for people who see both numbers' chats.
export type FilterGroup = 'show' | 'brand' | 'status' | 'owner' | 'area' | 'label';
export type ListFilter = Record<FilterGroup, string[]>;

const KEY = 'chats.listFilter';
export const ALL_OPEN = 'All open chats';
const EMPTY: ListFilter = { show: [], brand: [], status: [], owner: [], area: [], label: [] };
let current: ListFilter = read();
const listeners = new Set<() => void>();

function read(): ListFilter {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Partial<ListFilter>;
    return {
      show: Array.isArray(saved.show) ? saved.show : [],
      brand: Array.isArray(saved.brand) ? saved.brand : [],
      status: Array.isArray(saved.status) ? saved.status : [],
      owner: Array.isArray(saved.owner) ? saved.owner : [],
      area: Array.isArray(saved.area) ? saved.area : [],
      label: Array.isArray(saved.label) ? saved.label : [],
    };
  } catch {
    return EMPTY;
  }
}

function save(next: ListFilter) {
  current = next;
  try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* storage blocked: this visit only */ }
  for (const l of listeners) l();
}

export function toggleFilter(group: FilterGroup, value: string) {
  const on = current[group].includes(value);
  save({ ...current, [group]: on ? current[group].filter((v) => v !== value) : [...current[group], value] });
}

export function clearFilter() {
  save(EMPTY);
}

export function useListFilter(): ListFilter {
  return useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => current);
}

/** A chat's value in each group, as the sheet lists them. */
export function filterValues(c: Chat): Record<FilterGroup, string[]> {
  const status = customerStatus(c);
  // No owner moved here from the tabs (Gary, 19 Oct); Leo is a tab of its own now.
  const owner = c.owner ?? 'No owner';
  return {
    show: [ALL_OPEN],
    brand: c.isTeam ? [] : [BRAND_NAME[c.brand]],
    // A graded enquiry is under its grade, as its chip shows, and New is only the ungraded (Gary, 28 Oct).
    status: status ? [status === 'New' && c.grade && !c.customerId ? GRADE_NAME[c.grade] : status] : [],
    owner: c.isTeam ? [] : [owner],
    area: c.area ? [c.area] : [],
    label: (c.labels ?? []).map((l) => l.name),
  };
}

export const BRAND_NAME: Record<Chat['brand'], string> = { cmb: 'CleanMyBed', pos: 'Peace of Sleep' };

const GRADE_NAME: Record<NonNullable<Chat['grade']>, string> = { hot: 'Hot', warm: 'Warm', cold: 'Cold' };

export function filterCount(f: ListFilter): number {
  // "All open chats" no longer narrows anything once the tabs went (Gary, 23 Oct), so a kept one is not counted.
  return f.brand.length + f.status.length + f.owner.length + f.area.length + f.label.length;
}

export function passesFilter(c: Chat, f: ListFilter): boolean {
  if (!filterCount(f)) return true;
  const v = filterValues(c);
  return (Object.keys(f) as FilterGroup[]).every((g) => !f[g].length || v[g].some((x) => f[g].includes(x)));
}
